import { DateValue } from "@mantine/dates"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FilterChipData } from "../odoo/filter-chip"

export type ShippingFilters = {
  refCode?: string
  loadingDate?: [DateValue, DateValue]
  pullSheetStatus?: string | null
  deliveryDate?: [DateValue, DateValue]
  staffFirstNameEmail?: string
  deliveryNoteCount?: string | number
  warehouseRefCodeName?: string
  searchQuery?: string
}

const initialFilter: ShippingFilters = {
  refCode: "",
  loadingDate: [null, null],
  pullSheetStatus: null,
  deliveryDate: [null, null],
  staffFirstNameEmail: "",
  deliveryNoteCount: "",
  warehouseRefCodeName: "",
  searchQuery: "",
}

type FilterLabelMap = { key: keyof ShippingFilters; label: string; format?: (val: any) => string | null }

function useFilterLabels(): FilterLabelMap[] {
  const { t } = useTranslation(["delivery", "common"])
  return [
    { key: "refCode", label: t("delivery:filter.loadingNo") },
    { key: "pullSheetStatus", label: t("delivery:filter.loadingStatus"), format: (val: string | null) => (val ? t(`delivery:psStatus.${val}`, { defaultValue: "" }) || null : null) },
    { key: "staffFirstNameEmail", label: t("delivery:filter.responsible") },
    { key: "warehouseRefCodeName", label: t("delivery:filter.warehouse") },
    { key: "searchQuery", label: t("common:filter.search", { ns: "common" }) },
    {
      key: "loadingDate", label: t("delivery:filter.loadingDate"),
      format: (val: [DateValue, DateValue]) => (!val || (!val[0] && !val[1])) ? null : `${val[0] || "..."} - ${val[1] || "..."}`,
    },
    {
      key: "deliveryDate", label: t("delivery:filter.deliveryDate"),
      format: (val: [DateValue, DateValue]) => (!val || (!val[0] && !val[1])) ? null : `${val[0] || "..."} - ${val[1] || "..."}`,
    },
  ]
}

function getDisplayValue(val: any): string | null {
  if (val === null || val === undefined || val === "") return null
  if (Array.isArray(val)) return val.every((v) => !v) ? null : val.filter(Boolean).join(" - ")
  return String(val)
}

export function useShippingFilters() {
  const [filters, setFilters] = useState<ShippingFilters>(structuredClone(initialFilter))
  const FILTER_LABELS = useFilterLabels()
  const setFilter = <K extends keyof ShippingFilters>(key: K, value: ShippingFilters[K]) => setFilters((prev) => ({ ...prev, [key]: value }))
  const clearFilters = () => setFilters(structuredClone(initialFilter))
  const removeFilter = (key: keyof ShippingFilters) => setFilters((prev) => ({ ...prev, [key]: (initialFilter as any)[key] }))

  const activeFilterChips: FilterChipData[] = FILTER_LABELS.reduce<FilterChipData[]>((chips, item) => {
    const val = filters[item.key]
    const display = item.format ? item.format(val) : getDisplayValue(val)
    if (display) chips.push({ key: item.key, label: item.label, value: display, onRemove: () => removeFilter(item.key) })
    return chips
  }, [])

  const { searchQuery, ...apiFilters } = filters
  return { filters: apiFilters, setFilter, clearFilters, activeFilterChips, searchQuery }
}
